var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useCallback, useEffect } from "react";
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography, alpha, } from "@mui/material";
import { PageContainer, PageContent } from "../../components/wraper";
import { PrimaryGrid } from "../../components/grid/PrimaryGrid";
import { GridFilters } from "../../components/grid/GridFilters";
import PageHeader from "@ui/components/header/PageHeader";
import { SButton } from "@ui/components/buttons/SButton";
import { DeviceDetailsRow, DeviceStatusIndicator, getDeviceDetailsMinWidth, } from "@ui/components/grid/DeviceDetailsRow";
import { GridCellStack } from "@ui/components/grid";
import { CloseIcon, TrashIcon } from "@ui/components/icons/ActionIcons";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
import { useNodesFilter } from "./hooks/useNodesFilter";
import { useNodesActions } from "./hooks/useNodesActions";
import { useNodesFilterDropdowns } from "./hooks/useNodesFilterDropdowns";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "@application/store/useStore";
import { updateFilters } from "@reducers/nodes/slice/nodes-slice";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { categoriesApi } from "@api/enpoints/categories-api";
import { NodeActionsDialog } from "../dashboard/ap-details/components/NodeActionsDialog";
import { forceDisconnectNode, } from "@reducers/nodes/thunk/nodes-thunk";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { NodeCard } from "./components/NodeCard";
import { READING_COLUMN_WIDTH } from "../dashboard/ap-details/components/APReadingsSection";
import { NodeExternalTemperatureTile, NodeHumidityTile, NodeStatusCell, NodeTemperatureTile, nodeGridRowDisplay, } from "../dashboard/ap-details/components/NodeReadingCells";
/** 36px round icon button used in the row actions (Figma: IconButton). */
const RowIconButton = ({ title, onClick, disabled, tinted, children, }) => (React.createElement(Tooltip, { title: title },
    React.createElement("span", null,
        React.createElement(IconButton, { "aria-label": title, disabled: disabled, onClick: onClick, sx: {
                width: 36,
                height: 36,
                p: 0,
                borderRadius: "500px",
                backgroundColor: (theme) => tinted ? alpha(theme.palette.error.main, 0.08) : "transparent",
                "&:hover": {
                    backgroundColor: (theme) => tinted
                        ? alpha(theme.palette.error.main, 0.16)
                        : alpha(theme.palette.grey[500], 0.08),
                },
            } }, children))));
const NodesPage = () => {
    var _a, _b;
    const { t } = useTranslation(["form", "validation", "common"]);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    // Note: SignalR for temperature updates is now handled globally in App.tsx via useSignalR hook
    // Dialog state for disconnect confirmation
    const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    // Delete node dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [nodeToDelete, setNodeToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    // Use existing custom hooks
    const { nodes, loading, searchTerm, filters, paginationInfo, currentPageNumber, currentPageSize, handleFilterChange, handleSearchChange, handleToggleActive, handleClearFilters, handlePageChange, handlePageSizeChange, fetchNodes, } = useNodesFilter();
    // Handle URL query parameters (e.g., ?accessPointUid=xxx)
    useEffect(() => {
        const accessPointUid = searchParams.get("accessPointUid");
        if (accessPointUid) {
            // Set the filter for the access point
            dispatch(updateFilters({ accessPointUid: [accessPointUid] }));
            // Remove the query parameter from URL to keep it clean
            searchParams.delete("accessPointUid");
            navigate({ search: searchParams.toString() }, { replace: true });
        }
    }, [searchParams, dispatch, navigate]);
    // Use filter dropdowns hook
    const { options: dropdownOptions, loading: dropdownLoading, searchNodeUids, searchNodeNames, searchApUids, searchApNames, loadInitialOptions, } = useNodesFilterDropdowns();
    const { handleAddNode, handleEditNode, handleDisconnectNode, handleDeleteNode, handleConfigureAPDevice, } = useNodesActions();
    // Handle dropdown input change with debounce
    const handleDropdownInputChange = useCallback((field, inputValue) => {
        switch (field) {
            case "nodeUid":
                searchNodeUids(inputValue);
                break;
            case "nodeName":
                searchNodeNames(inputValue);
                break;
            case "accessPointUid":
                searchApUids(inputValue);
                break;
            case "accessPointName":
                searchApNames(inputValue);
                break;
        }
    }, [searchNodeUids, searchNodeNames, searchApUids, searchApNames]);
    // Helper to get filter options with selected values included
    const getFilterOptionsWithSelected = useCallback((searchResults, selectedValues) => {
        const searchOptions = searchResults.map((item) => ({
            value: item,
            label: item,
        }));
        // Add selected values that aren't in search results
        if (selectedValues) {
            const selectedArray = Array.isArray(selectedValues)
                ? selectedValues
                : [selectedValues];
            const existingValues = new Set(searchResults);
            const selectedOptions = selectedArray
                .filter((val) => !existingValues.has(val))
                .map((val) => ({ value: val, label: val }));
            return [...selectedOptions, ...searchOptions];
        }
        return searchOptions;
    }, []);
    // Simple menu handlers
    // Filter dialog definitions only — what a row looks like is defined in `rowColumns`.
    const columns = [
        {
            field: "nodeId",
            headerName: t("common:grid.nodeUid"),
            filterable: true,
            filterType: "asyncSelect",
            filterField: "nodeUid", // Backend filter field name
            filterOptions: getFilterOptionsWithSelected(dropdownOptions.nodeUids, filters.nodeUid),
            filterLoading: dropdownLoading.nodeUids,
            onFilterInputChange: (value) => handleDropdownInputChange("nodeUid", value),
        },
        {
            field: "nodeName",
            headerName: t("common:grid.nodeName"),
            filterable: true,
            filterType: "asyncSelect",
            filterOptions: getFilterOptionsWithSelected(dropdownOptions.nodeNames, filters.nodeName),
            filterLoading: dropdownLoading.nodeNames,
            onFilterInputChange: (value) => handleDropdownInputChange("nodeName", value),
        },
        {
            field: "status",
            headerName: t("common:grid.status"),
            filterable: true,
            filterType: "select",
            filterOptions: ["active", "inactive"],
        },
        {
            field: "deviceId",
            headerName: t("common:grid.apId"),
            filterable: true,
            filterType: "asyncSelect",
            filterField: "accessPointUid", // Backend filter field name
            filterOptions: getFilterOptionsWithSelected(dropdownOptions.apUids, filters.accessPointUid),
            filterLoading: dropdownLoading.apUids,
            onFilterInputChange: (value) => handleDropdownInputChange("accessPointUid", value),
        },
        {
            field: "deviceName",
            headerName: t("common:grid.apName"),
            filterable: true,
            filterType: "asyncSelect",
            filterField: "accessPointName", // Backend filter field name
            filterOptions: getFilterOptionsWithSelected(dropdownOptions.apNames, filters.accessPointName),
            filterLoading: dropdownLoading.apNames,
            onFilterInputChange: (value) => handleDropdownInputChange("accessPointName", value),
        },
    ];
    // Navigate to the AP profile (same as Dashboard) unless the click hit a control.
    const handleRowClick = (row, event) => {
        const target = event.target;
        if (target.closest('[data-field="actions"]') ||
            target.closest("button") ||
            target.closest('[role="button"]')) {
            return;
        }
        // deviceId is the accessPointUid from the node
        if (row.deviceId) {
            navigate(`/dashboard/${row.deviceId}`);
        }
    };
    // Layout of one table row (Figma: NodesRow). Widths mirror the design.
    const rowColumns = [
        {
            field: "node",
            width: 123,
            renderCell: (params) => React.createElement(NodeTemperatureTile, { row: params.row }),
        },
        {
            field: "nodeName",
            flex: 3,
            minWidth: 142.5,
            // The External Temperature Sensor Name replaces the node name when the external temperature is the main one.
            renderCell: (params) => {
                const title = nodeGridRowDisplay(params.row, t).title;
                return (React.createElement(Box, { sx: { width: "100%", minWidth: 0 } },
                    React.createElement(Tooltip, { title: title },
                        React.createElement(Typography, { variant: "body2", noWrap: true }, title)),
                    React.createElement(Typography, { variant: "body2", color: "text.secondary", noWrap: true }, params.row.nodeId)));
            },
        },
        {
            field: "deviceName",
            flex: 3,
            minWidth: 142.5,
            renderCell: (params) => {
                var _a;
                return (React.createElement(Box, { sx: { width: "100%", minWidth: 0 } },
                    React.createElement(Tooltip, { title: (_a = params.row.deviceName) !== null && _a !== void 0 ? _a : "" },
                        React.createElement(Typography, { variant: "body2", noWrap: true }, params.row.deviceName)),
                    React.createElement(Typography, { variant: "body2", color: "text.secondary", noWrap: true }, params.row.deviceId)));
            },
        },
        {
            field: "externalTemperature",
            flex: 1,
            minWidth: READING_COLUMN_WIDTH,
            align: "center",
            renderCell: (params) => React.createElement(NodeExternalTemperatureTile, { row: params.row }),
        },
        {
            field: "humidity",
            flex: 1,
            minWidth: READING_COLUMN_WIDTH,
            align: "center",
            renderCell: (params) => React.createElement(NodeHumidityTile, { row: params.row }),
        },
        {
            // Status chip, with the free roaming icon in front of it.
            field: "temperature",
            flex: 1,
            minWidth: 130,
            align: "right",
            renderCell: (params) => React.createElement(NodeStatusCell, { row: params.row }),
        },
        {
            field: "status",
            flex: 1,
            minWidth: 80,
            align: "center",
            renderCell: (params) => (React.createElement(DeviceStatusIndicator, { active: Boolean(params.row.isActive) })),
        },
        {
            field: "lastUpdate",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (React.createElement(GridCellStack, { topText: formatUtcToLocalDate(params.row.lastUpdate), bottomText: formatUtcToLocalTime(params.row.lastUpdate) })),
        },
        {
            field: "actions",
            width: 328,
            renderCell: (params) => {
                var _a, _b;
                const isAlreadyDisconnected = !((_a = params.row.deviceId) === null || _a === void 0 ? void 0 : _a.trim());
                return (React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center" },
                    PermissionChecker.hasPermission(Permission.ACCESSPOINTS_DELETE) && (React.createElement(RowIconButton, { title: isAlreadyDisconnected
                            ? t("common:actions.nodeAlreadyDisconnected")
                            : t("common:actions.disconnectNode"), disabled: isAlreadyDisconnected, onClick: () => {
                            setSelectedNode({
                                id: params.row.id,
                                nodeName: params.row.nodeName,
                                nodeId: params.row.nodeId,
                                nodeTemperature: params.row.nodeTemperature,
                                deviceName: params.row.deviceName,
                            });
                            setIsDisconnectDialogOpen(true);
                        } },
                        React.createElement(CloseIcon, null))),
                    PermissionChecker.hasPermission(Permission.NODES_DELETE) && (React.createElement(RowIconButton, { title: t("common:actions.delete"), tinted: true, onClick: () => {
                            setNodeToDelete({
                                id: params.row.id,
                                nodeName: params.row.nodeName,
                                nodeId: params.row.nodeId,
                            });
                            setDeleteDialogOpen(true);
                            setDeleteError(null);
                        } },
                        React.createElement(TrashIcon, null))),
                    React.createElement(SButton, { size: "medium", variant: "soft", onClick: () => {
                            var _a;
                            const ap = (_a = params.row.deviceId) === null || _a === void 0 ? void 0 : _a.trim();
                            navigate(ap
                                ? `/nodes/${params.row.nodeId}/logs/${ap}`
                                : `/nodes/${params.row.nodeId}/logs`);
                        } }, t("common:actions.logs")),
                    PermissionChecker.hasPermission(Permission.NODES_EDIT) && (React.createElement(SButton, { variant: "soft", color: "primary", size: "medium", onClick: () => handleEditNode(params.row.id) }, t("common:actions.edit"))),
                    PermissionChecker.hasPermission(Permission.ACCESSPOINTS_EDIT) && (React.createElement(SButton, { variant: "outlined", color: "primary", size: "medium", onClick: () => handleConfigureAPDevice(params.row.deviceId), disabled: !((_b = params.row.deviceId) === null || _b === void 0 ? void 0 : _b.trim()) }, "AP"))));
            },
        },
    ];
    // Node Category: a filter-dialog field only (not a grid column); the filtering runs on the server.
    const [categoryOptions, setCategoryOptions] = useState([]);
    useEffect(() => {
        let active = true;
        categoriesApi
            .getOptions("node")
            .then((response) => { var _a; return active && setCategoryOptions(((_a = response.data.data) !== null && _a !== void 0 ? _a : []).map((c) => ({ value: c.id, label: c.name }))); })
            .catch(() => active && setCategoryOptions([]));
        return () => {
            active = false;
        };
    }, []);
    const filterColumns = [
        ...columns,
        {
            field: "nodeCategoryIds",
            headerName: t("common:catalog.categories.title", "Categories"),
            filterable: true,
            filterType: "select",
            filterOptions: categoryOptions,
        },
    ];
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.nodes") }),
        React.createElement(PageContent, null,
            React.createElement(GridFilters, { title: `${(_a = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _a !== void 0 ? _a : nodes.length} ${t("common:pages.nodes")}`, loading: loading, columns: filterColumns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange, onFilterOpen: loadInitialOptions, onApplyFilters: fetchNodes }),
            React.createElement(PrimaryGrid, { rows: loading ? [] : nodes, columns: columns, loading: loading, heightVariant: "full", checkboxSelection: false, autoHeight: true, paginationMode: "server", minContentWidth: getDeviceDetailsMinWidth(rowColumns), renderRow: (row) => (React.createElement(Box, { sx: { mb: 1 } },
                    React.createElement(DeviceDetailsRow, { columns: rowColumns, onClick: (event) => handleRowClick(row, event), renderCell: (col) => {
                            var _a, _b;
                            return (_b = (_a = col.renderCell) === null || _a === void 0 ? void 0 : _a.call(col, {
                                row,
                                value: row[col.field],
                                field: col.field,
                                id: row.id,
                            })) !== null && _b !== void 0 ? _b : null;
                        } }))), renderSkeletonRow: () => (React.createElement(Skeleton, { variant: "rounded", height: 88, sx: { borderRadius: "32px", mb: 1 } })), renderCard: (row) => (React.createElement(NodeCard, { row: row, onDisconnect: (node) => {
                        setSelectedNode({
                            id: node.id,
                            nodeName: node.nodeName,
                            nodeId: node.nodeId,
                            nodeTemperature: node.nodeTemperature,
                            deviceName: node.deviceName,
                        });
                        setIsDisconnectDialogOpen(true);
                    }, onDelete: (node) => {
                        setNodeToDelete({
                            id: node.id,
                            nodeName: node.nodeName,
                            nodeId: node.nodeId,
                        });
                        setDeleteDialogOpen(true);
                        setDeleteError(null);
                    }, onLogs: (nodeId, deviceId) => {
                        const ap = deviceId === null || deviceId === void 0 ? void 0 : deviceId.trim();
                        navigate(ap
                            ? `/nodes/${nodeId}/logs/${ap}`
                            : `/nodes/${nodeId}/logs`);
                    }, onEdit: (id) => handleEditNode(id), onApConfig: (deviceId) => handleConfigureAPDevice(deviceId), onClick: (deviceId) => {
                        if (deviceId)
                            navigate(`/dashboard/${deviceId}`);
                    } })), rowCount: (_b = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _b !== void 0 ? _b : 0, paginationModel: {
                    page: currentPageNumber - 1,
                    pageSize: currentPageSize,
                }, onPaginationModelChange: (model) => {
                    handlePageChange(model.page + 1);
                    handlePageSizeChange(model.pageSize);
                }, pageSizeOptions: [25, 50] }),
            React.createElement(NodeActionsDialog, { isOpen: isDisconnectDialogOpen, onClose: () => {
                    setIsDisconnectDialogOpen(false);
                    setSelectedNode(null);
                }, onConfirm: () => __awaiter(void 0, void 0, void 0, function* () {
                    if (!selectedNode)
                        return;
                    setIsDisconnecting(true);
                    try {
                        yield dispatch(forceDisconnectNode({
                            nodeId: selectedNode.id,
                        })).unwrap();
                        setIsDisconnectDialogOpen(false);
                        setSelectedNode(null);
                        setTimeout(() => {
                            fetchNodes();
                        }, 200);
                    }
                    catch (error) {
                        console.error("Failed to disconnect node:", error);
                    }
                    finally {
                        setIsDisconnecting(false);
                    }
                }), selectedNode: selectedNode, action: "disconnect", accessPointName: selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.deviceName }),
            React.createElement(ConfirmDialog, { isOpen: deleteDialogOpen, onClose: () => {
                    if (!deleteLoading) {
                        setDeleteDialogOpen(false);
                        setNodeToDelete(null);
                        setDeleteError(null);
                    }
                }, onConfirm: () => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b;
                    if (!nodeToDelete)
                        return;
                    setDeleteError(null);
                    setDeleteLoading(true);
                    try {
                        yield handleDeleteNode(nodeToDelete.id);
                        setDeleteDialogOpen(false);
                        setNodeToDelete(null);
                        setTimeout(() => fetchNodes(), 200);
                    }
                    catch (err) {
                        setDeleteError((err === null || err === void 0 ? void 0 : err.message) ||
                            ((_b = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) ||
                            "Failed to delete node");
                    }
                    finally {
                        setDeleteLoading(false);
                    }
                }), loading: deleteLoading, variant: "delete", actionText: t("common:actions.delete"), screenName: nodeToDelete
                    ? `${t("common:grid.nodeName")} "${nodeToDelete.nodeName || nodeToDelete.nodeId}"`
                    : "", customMessage: deleteError !== null && deleteError !== void 0 ? deleteError : undefined }))));
};
export default NodesPage;
